import { deleteField } from "firebase/firestore";

import {
  HERO_HEADLINE_MAX,
  HERO_KICKER_MAX,
  HERO_STAT_LABEL_MAX,
  HERO_STAT_VALUE_MAX,
  HERO_SUBLINE_MAX,
  STAT_SLOTS,
  type StatSlot,
} from "../events/landing";
import type { EventSettings } from "./schemas";
import type { EventSettingsWrite } from "./use-event-settings-writes";

export type StatTile = { value: string; label: string };

/** What the Landing page card edits: the hero words and the number band. */
export type LandingForm = {
  kicker: string;
  headline: string;
  subline: string;
  stats: Record<StatSlot, StatTile>;
};

/** A message per field that would be refused; empty when the form is fine. */
export type LandingProblems = {
  kicker?: string;
  headline?: string;
  subline?: string;
  stats?: Partial<Record<StatSlot, string>>;
};

const HEADLINE_LINES = 3;
const EMPTY_TILE: StatTile = { value: "", label: "" };

export function landingFormFrom(event: EventSettings): LandingForm {
  const tile = (slot: StatSlot): StatTile => {
    const stored = event.heroStats?.[slot];
    return stored ? { value: stored.value, label: stored.label } : EMPTY_TILE;
  };
  return {
    kicker: event.heroKicker ?? "",
    headline: event.heroHeadline ?? "",
    subline: event.heroSubline ?? "",
    stats: { a: tile("a"), b: tile("b"), c: tile("c") },
  };
}

/**
 * The headline as it is stored: each line trimmed, empty lines dropped. What
 * the rules measure is this string, so the counter and the check use it too.
 */
export function normalizeHeadline(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n");
}

const trimTile = (tile: StatTile): StatTile => ({
  value: tile.value.trim(),
  label: tile.label.trim(),
});

/** Tile 1, 2 and 3 are the slots a, b and c, as the admin sees them. */
const tileName = (slot: StatSlot): string =>
  `Tile ${STAT_SLOTS.indexOf(slot) + 1}`;

/**
 * What firestore.rules would refuse, said per field. The rules answer with a
 * permission-denied that names no field, and they require both texts of a tile
 * or neither.
 */
export function landingProblems(form: LandingForm): LandingProblems {
  const problems: LandingProblems = {};
  if (form.kicker.trim().length > HERO_KICKER_MAX)
    problems.kicker = `The kicker must be ${HERO_KICKER_MAX} characters or fewer.`;
  const headline = normalizeHeadline(form.headline);
  if (headline.split("\n").length > HEADLINE_LINES)
    problems.headline = "The headline can have three lines at most.";
  else if (headline.length > HERO_HEADLINE_MAX)
    problems.headline = `The headline must be ${HERO_HEADLINE_MAX} characters or fewer.`;
  if (form.subline.trim().length > HERO_SUBLINE_MAX)
    problems.subline = `The subline must be ${HERO_SUBLINE_MAX} characters or fewer.`;

  const stats: Partial<Record<StatSlot, string>> = {};
  for (const slot of STAT_SLOTS) {
    const { value, label } = trimTile(form.stats[slot]);
    if (!value && !label) continue;
    if (!value || !label)
      stats[slot] =
        `${tileName(slot)} needs both a number and a label, or neither.`;
    else if (value.length > HERO_STAT_VALUE_MAX)
      stats[slot] =
        `${tileName(slot)}: the number must be ${HERO_STAT_VALUE_MAX} characters or fewer.`;
    else if (label.length > HERO_STAT_LABEL_MAX)
      stats[slot] =
        `${tileName(slot)}: the label must be ${HERO_STAT_LABEL_MAX} characters or fewer.`;
  }
  if (Object.keys(stats).length > 0) problems.stats = stats;
  return problems;
}

/**
 * The test id of the field to put focus on after a refused save: the first
 * wrong one in page order. In a tile it is the text that is missing or too
 * long, which is the one to fix.
 */
export function firstProblemField(
  form: LandingForm,
  problems: LandingProblems,
): string | null {
  if (problems.kicker) return "landing-kicker";
  if (problems.headline) return "landing-headline";
  if (problems.subline) return "landing-subline";
  for (const slot of STAT_SLOTS) {
    if (!problems.stats?.[slot]) continue;
    const { value } = trimTile(form.stats[slot]);
    const part =
      !value || value.length > HERO_STAT_VALUE_MAX ? "value" : "label";
    return `landing-stat-${slot}-${part}`;
  }
  return null;
}

/** The filled tiles by slot; a tile with both texts empty is not there. */
function filledTiles(
  tiles: Record<StatSlot, StatTile>,
): Partial<Record<StatSlot, StatTile>> {
  const filled: Partial<Record<StatSlot, StatTile>> = {};
  for (const slot of STAT_SLOTS) {
    const tile = trimTile(tiles[slot]);
    if (tile.value || tile.label) filled[slot] = tile;
  }
  return filled;
}

/**
 * Only the keys whose value differs from the stored one. A cleared field is
 * removed rather than stored empty: the rules would accept "", but a stored
 * empty string is one more value every reader has to defend against.
 *
 * `heroStats` is written whole, because the rules read it as one key. It is
 * removed when no tile is left.
 */
export function landingChanges(
  form: LandingForm,
  event: EventSettings,
): EventSettingsWrite {
  const changes: EventSettingsWrite = {};
  const text = (
    key: "heroKicker" | "heroHeadline" | "heroSubline",
    next: string,
    stored: string | undefined,
  ) => {
    if (next !== (stored ?? "")) changes[key] = next || deleteField();
  };
  text("heroKicker", form.kicker.trim(), event.heroKicker);
  text("heroHeadline", normalizeHeadline(form.headline), event.heroHeadline);
  text("heroSubline", form.subline.trim(), event.heroSubline);

  const next = filledTiles(form.stats);
  const stored = filledTiles(landingFormFrom(event).stats);
  if (JSON.stringify(next) !== JSON.stringify(stored))
    changes.heroStats = Object.keys(next).length > 0 ? next : deleteField();
  return changes;
}
